import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { FleetGrid } from '../../../../shared/sso-workspace/fleet-grid'
import { serverMessage } from '../../../../shared/error/server-message'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { TableAgInfinite } from '../../../../shared/table-builder/table-ag-infinite'
import { ColumnFormat, ColumnMenu, ColumnText } from '../../../../shared/table-builder/table-columns'
import { getQueryKeys } from '../../../../shared/table-builder/hooks'
import { Menu, MenuItem } from '../../../../shared/menu/menu'
import { OrgPicker } from '../../../../shared/org-search/org-picker'
import { IconNotification, IconRefresh, IconView, IconCancel } from '../../../../shared/icon/icon'
import { makeCursorRows, listDeliveries, parkDelivery, resendDelivery } from '../../../../api-new/state/notify'
import { CHANNELS, DELIVERY_STATUSES, LANES, PROVIDERS, channelLabel, deliveryStatusLabel, laneLabel, providerLabel } from '../../../../shared/notify/words'
import { agoCell, channelCell, deliveryStatusCell, monoCell, organizationCell, providerCell } from './cells'
import { ReasonDialog } from './reason-dialog'
import { useNotifyOpsAccess } from './access'
import { timelinePath } from './paths'
import './notify.scss'

const ALL_ORGANIZATIONS = { value: '', label: 'Every organization' }
const NO_FILTERS = { organizationId: '', channel: '', status: '', provider: '', lane: '', problems: false, from: '', to: '' }

// Statuses a delivery can be resent from, and parked from (C-4).
export const RESENDABLE = ['failed', 'expired', 'suppressed', 'parked']
export const PARKABLE = ['pending', 'retrying', 'failed']

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// The grid's search box: a trace (notification) id, a login id, or a type.
export const searchFilter = text => {
  const v = String(text || '').trim()
  if (!v) return {}
  if (UUID.test(v)) return { notificationId: v.toLowerCase() }
  if (/^\d{1,18}$/.test(v)) return { loginId: v }
  return { type: v }
}

const dayStart = value => {
  if (!value) return ''
  const [y, m, d] = value.split('-').map(Number)
  return new Date(y, m - 1, d).toISOString()
}
const dayAfter = value => {
  if (!value) return ''
  const [y, m, d] = value.split('-').map(Number)
  return new Date(y, m - 1, d + 1).toISOString()
}

// One page's query (C-4 GET /notify/deliveries). The organization view
// never names an organization: the server forces the caller's.
export const deliveryParams = ({ filters, system, search, limit, cursor, extra }) => ({
  scope: system ? 'system' : 'organization',
  limit,
  cursor,
  organizationId: system ? filters.organizationId : '',
  channel: filters.channel,
  status: filters.problems ? '' : filters.status,
  provider: filters.provider,
  lane: filters.lane,
  problems: filters.problems ? 'true' : '',
  since: dayStart(filters.from),
  until: dayAfter(filters.to),
  ...searchFilter(search),
  ...(extra || {})
})

// The delivery log (spec section 13.1): every delivery and what happened
// to it, newest first, in the table builder's server-paged grid. The staff
// page (`system`, SYS_NOTIFY read) sees every organization, with an
// Organization filter and column, and may resend and park; the
// organization's page (Email activity's rule: its own organization only,
// USER update) sees its own rows and no actions. The server enforces the
// scope the page names. A row opens its notification's timeline.
//
// fixed: filters the page sets for good (the dead-letter view's
// status=failed), and `title` / `lede` its heading.
export const DeliveryLog = ({ system = false, fixed, title = 'Delivery log', lede, exportName = 'notification-deliveries', allowStatus = true, toolbarExtra }) => {
  const history = useHistory()
  const access = useNotifyOpsAccess()
  const canAct = system && access.canUpdate
  const [filters, setFilters] = useState(NO_FILTERS)
  const [generation, setGeneration] = useState(0)
  const [error, failure, setError] = useErrorLine()
  const [notice, setNotice] = useState(null)
  const [action, setAction] = useState(null) // { kind: 'resend' | 'park', row }
  const [actionBusy, setActionBusy] = useState(false)
  const [actionError, actionFailure, setActionError] = useErrorLine()
  const tableRef = useRef()

  const rangeError = filters.from && filters.to && filters.from > filters.to ? 'From must be on or before To.' : null
  const fixedKey = JSON.stringify(fixed || {})
  const gridKey = `${generation}:${JSON.stringify(filters)}:${fixedKey}`
  const currentKey = useRef(gridKey)
  currentKey.current = gridKey

  const getRows = useMemo(
    () => {
      const mine = gridKey
      // One cursor chain per search: a new search starts its own.
      const chains = new Map()
      const chainFor = search => {
        if (!chains.has(search)) {
          chains.set(search, makeCursorRows({
            isCurrent: () => currentKey.current === mine,
            load: ({ limit, cursor }) => listDeliveries(deliveryParams({ filters, system, search, limit, cursor, extra: fixed }))
          }))
        }
        return chains.get(search)
      }
      return async (start, end, search) => {
        if (rangeError) return { data: [], count: 0 }
        try {
          setError(null)
          return await chainFor(search || '')(start, end)
        } catch (err) {
          if (currentKey.current !== mine) return undefined
          setError(serverMessage(err, start === 0 ? "Deliveries couldn't be loaded." : "Older deliveries couldn't be loaded."), err)
          return { data: [], count: start }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gridKey, system, rangeError]
  )

  const set = (key, value) => setFilters(f => ({ ...f, [key]: value }))

  const openTimeline = useCallback(row => {
    if (row && row.notificationId) history.push(timelinePath(row.notificationId, system))
  }, [history, system])

  const clear = () => {
    const keys = getQueryKeys()
    const params = new URLSearchParams(history.location.search)
    params.delete(keys.search)
    params.delete(keys.index)
    history.replace({ search: `${params}` })
    setFilters(NO_FILTERS)
    setGeneration(g => g + 1)
  }

  const runAction = async reason => {
    if (!action) return
    setActionBusy(true)
    setActionError(null)
    try {
      const result = action.kind === 'resend' ? await resendDelivery(action.row.id, reason) : await parkDelivery(action.row.id, reason)
      setNotice(action.kind === 'resend'
        ? `Sent again as delivery ${result && result.id ? result.id : 'new'}.`
        : `Delivery ${action.row.id} is parked: it won't be tried again.`)
      setAction(null)
      if (tableRef.current && tableRef.current.api) tableRef.current.api.refreshInfiniteCache()
      else setGeneration(g => g + 1)
    } catch (err) {
      setActionError(serverMessage(err, action.kind === 'resend' ? "The delivery couldn't be resent." : "The delivery couldn't be parked."), err)
    } finally {
      setActionBusy(false)
    }
  }

  const rowMenu = useMemo(
    () => r =>
      <Menu small>
        <MenuItem Icon={IconView} text="timeline" pin show onClick={() => openTimeline(r)} />
        <MenuItem Icon={IconRefresh} text="resend" pin show={canAct && RESENDABLE.includes(r.status)} onClick={() => { setActionError(null); setAction({ kind: 'resend', row: r }) }} />
        <MenuItem Icon={IconCancel} text="park" pin show={canAct && PARKABLE.includes(r.status)} onClick={() => { setActionError(null); setAction({ kind: 'park', row: r }) }} />
      </Menu>,
    [canAct, openTimeline, setActionError]
  )

  const filtered = Object.entries(filters).some(([k, v]) => k !== 'problems' && Boolean(v))

  const emptyState = {
    Icon: IconNotification,
    title: rangeError ? 'Check the dates' : filtered ? 'No matches' : filters.problems ? 'No problems' : 'No deliveries yet',
    message: rangeError ? 'Fix the dates to see deliveries.' : filtered ? 'No delivery matches these filters.'
      : filters.problems ? 'Nothing failed, expired, was suppressed or parked, and nothing is stuck retrying.' : 'Email and push deliveries appear here as notifications go out.',
    action: { label: 'Clear filters', onClick: clear, show: Boolean(filtered || filters.problems) }
  }
  const noMatchState = { Icon: IconNotification, title: 'No matches', message: 'No delivery matches this search.', action: { label: 'Clear filters', onClick: clear } }

  return (
    <section className="sw-section" aria-labelledby="nf-deliveries-title">
      <div className="sw-subh mb-1">
        <h2 className="h6 fw-semibold m-0" id="nf-deliveries-title">{title}</h2>
        <div className="d-flex flex-wrap gap-2">
          {toolbarExtra}
          {allowStatus &&
            <button type="button" className="btn btn-sm sw-btn" aria-pressed={filters.problems ? 'true' : 'false'} onClick={() => set('problems', !filters.problems)}>Problems</button>}
        </div>
      </div>
      <p className="sw-hint mb-3" style={{ maxWidth: '72ch' }}>
        {lede || (system
          ? 'Every email and push delivery, newest first. Search by trace id, login id or notification type. Open one to see its whole timeline.'
          : 'Email and push notifications EducationXR sent for your organization, newest first. Search by login id or notification type.')}
      </p>

      {error && <p className="text-danger small" role="alert">{error}</p>}
      {error && <ErrorReference error={failure} className="mb-2" />}
      {rangeError && <p className="text-danger small" role="alert">{rangeError}</p>}
      {notice && <p className="text-success small" role="status">{notice}</p>}

      <div className="nf-toolbar">
        {system &&
          <span className="nf-org">
            <OrgPicker id="nf-org" label="Organization" value={filters.organizationId} onChange={v => set('organizationId', v)} allOption={ALL_ORGANIZATIONS} small />
          </span>}
        <select className="form-select form-select-sm" aria-label="Channel" value={filters.channel} onChange={e => set('channel', e.target.value)}>
          <option value="">Every channel</option>
          {CHANNELS.filter(c => c !== 'inapp').map(c => <option key={c} value={c}>{channelLabel(c)}</option>)}
        </select>
        {allowStatus &&
          <select className="form-select form-select-sm" aria-label="Status" value={filters.status} disabled={filters.problems} onChange={e => set('status', e.target.value)}>
            <option value="">Every status</option>
            {DELIVERY_STATUSES.map(s => <option key={s} value={s}>{deliveryStatusLabel(s)}</option>)}
          </select>}
        <select className="form-select form-select-sm" aria-label="Provider" value={filters.provider} onChange={e => set('provider', e.target.value)}>
          <option value="">Every provider</option>
          {PROVIDERS.map(p => <option key={p} value={p}>{providerLabel(p)}</option>)}
        </select>
        {system &&
          <select className="form-select form-select-sm" aria-label="Lane" value={filters.lane} onChange={e => set('lane', e.target.value)}>
            <option value="">Every lane</option>
            {LANES.map(l => <option key={l} value={l}>{laneLabel(l)}</option>)}
          </select>}
        <span className="nf-field">
          <label htmlFor="nf-from">From</label>
          <input id="nf-from" type="date" className="form-control form-control-sm" value={filters.from} onChange={e => set('from', e.target.value)} />
        </span>
        <span className="nf-field">
          <label htmlFor="nf-to">To</label>
          <input id="nf-to" type="date" className="form-control form-control-sm" value={filters.to} onChange={e => set('to', e.target.value)} />
        </span>
      </div>

      <FleetGrid title="deliveries" tableRef={tableRef} exportName={exportName}>
        <TableAgInfinite key={gridKey} ref={tableRef} onGetRows={getRows} onRowClicked={openTimeline} emptyState={emptyState} noMatchState={noMatchState}>
          <ColumnFormat field="createdAt" header="When" render={agoCell} sortable={false} />
          <ColumnText field="type" header="Type" sortable={false} />
          <ColumnFormat field="channel" header="Channel" render={channelCell} sortable={false} fit="content" />
          <ColumnText field="addressMasked" header="To" sortable={false} />
          {system && <ColumnFormat field="organizationId" header="Organization" render={organizationCell} sortable={false} />}
          <ColumnFormat field="provider" header="Provider" render={providerCell} sortable={false} fit="content" />
          <ColumnFormat field="status" header="Status" render={deliveryStatusCell} sortable={false} fit="content" />
          <ColumnFormat field="attempts" header="Attempts" render={monoCell} sortable={false} fit="content" />
          {system && <ColumnFormat field="id" header="Delivery" render={monoCell} sortable={false} fit="content" />}
          <ColumnMenu menu={rowMenu} />
        </TableAgInfinite>
      </FleetGrid>

      <ReasonDialog
        open={Boolean(action)}
        title={action && action.kind === 'resend' ? 'Resend this delivery?' : 'Park this delivery?'}
        body={action && action.kind === 'resend'
          ? `Delivery ${action.row.id} (${action.row.addressMasked || action.row.channel}) is sent again as a new delivery. The original stays as it is. This is recorded.`
          : action ? `Delivery ${action.row.id} stops: it won't be tried again until someone resends it. This is recorded.` : ''}
        confirmText={action && action.kind === 'resend' ? 'Resend' : 'Park'}
        danger={Boolean(action && action.kind === 'park')}
        busy={actionBusy}
        error={actionError}
        failure={actionFailure}
        onConfirm={runAction}
        onCancel={() => setAction(null)}
      />
    </section>
  )
}
